import { Button, Col, Flex, Form, InputNumber, Row, Select } from "antd";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { COMPARISON_TYPE_OPTIONS } from "~pages/LeaderCriteria/constants/comparisonTypeSymbols.ts";
import { ComparisonType } from "~pages/LeaderCriteria/enums/comparisonType.ts";
import { useGetStatisticFieldOptions } from "~pages/LeaderCriteria/hooks/useGetStatisticFieldOptions.ts";
import { useLeaderCriteriaRuleService } from "~pages/LeaderCriteria/hooks/useLeaderCriteriaRuleService.ts";
import type { LeaderCriteriaRuleModel } from "~pages/LeaderCriteria/types/leaderCriteriaRuleModel.ts";

const LeaderCriteriaRuleManagePage = () => {
  const { customerId, sport, organizationId, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<LeaderCriteriaRuleModel>();
  const statisticFieldOptions = useGetStatisticFieldOptions();
  const { createLeaderCriteriaRule, isCreatePending } = useLeaderCriteriaRuleService();

  const ruleListUrl = `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${leaderCriteriaId}/qualifiers`;
  const pageTitle = formatMessage({ id: "pageTitles.organization.leader.add.rules" });

  const requiredRule = (fieldIntlKey: string) => [
    {
      required: true,
      message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: fieldIntlKey }) }),
    },
  ];

  const handleSubmit = async () => {
    const formValues = await form.validateFields();

    await createLeaderCriteriaRule(formValues);
  };

  return (
    <Page title={pageTitle} tabTitle={pageTitle}>
      <Row>
        <Col span={12}>
          <Flex vertical gap="middle">
            <Form
              form={form}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              initialValues={{
                statisticField: statisticFieldOptions[0]?.value,
                comparisonField: statisticFieldOptions[0]?.value,
                comparisonType: ComparisonType.EQUAL,
                comparisonValue: 0,
              }}
            >
              <Form.Item
                name="statisticField"
                label={formatMessage({ id: "statistic.field" })}
                tooltip={formatMessage({ id: "form.help.qualification.rule.statistic.field" })}
                style={formItemSharedStyles}
                rules={requiredRule("statistic.field")}
              >
                <Select showSearch optionFilterProp="label" options={statisticFieldOptions} />
              </Form.Item>
              <Form.Item
                name="comparisonField"
                label={formatMessage({ id: "comparison.field" })}
                tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.field" })}
                style={formItemSharedStyles}
                rules={requiredRule("comparison.field")}
              >
                <Select showSearch optionFilterProp="label" options={statisticFieldOptions} />
              </Form.Item>
              <Form.Item
                name="comparisonType"
                label={formatMessage({ id: "comparison.type" })}
                tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.type" })}
                style={formItemSharedStyles}
                rules={requiredRule("comparison.type")}
              >
                <Select options={COMPARISON_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="comparisonValue"
                label={formatMessage({ id: "comparison.value" })}
                tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.value" })}
                style={formItemSharedStyles}
                rules={requiredRule("comparison.value")}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Form>
            <Flex justify="flex-end" gap="large">
              <PermissionCheck permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]} permissionOptions={[PermissionOperations.CREATE]}>
                <Button type="primary" onClick={() => void handleSubmit()} disabled={isCreatePending}>
                  {formatMessage({ id: "leaders.rule.create" })}
                </Button>
              </PermissionCheck>
              <Link to={ruleListUrl}>
                <Button type="primary">{formatMessage({ id: "close" })}</Button>
              </Link>
            </Flex>
          </Flex>
        </Col>
      </Row>
    </Page>
  );
};

export default LeaderCriteriaRuleManagePage;
